<template>
  <el-tabs v-model="activeTab" class="group-details-tabs" tab-position="top">
    <!-- Assigned Software Tab -->
    <el-tab-pane name="software" :label="i18n.t('assignedSoftware')">
      <div class="software-list">
        <!-- Empty State -->
        <div v-if="assignedSoftware.length === 0" class="software-list__empty">
          <div class="software-list__empty-icon">
            <Package :size="32" class="text-contrast-muted" />
          </div>
          <p class="software-list__empty-text">
            {{ i18n.t('noAppsAssigned') }}
          </p>
        </div>

        <!-- Software Items -->
        <div class="software-list__items">
          <div
            v-for="(app, index) in assignedSoftware"
            :key="app.id"
            class="software-item">
            <!-- App Icon -->
            <div class="software-item__icon-wrapper">
              <ApplicationIcon
                :application-name="app.name"
                :size="36"
                :application-type="app.type"
                class="software-item__icon" />
            </div>

            <!-- App Info -->
            <div class="software-item__info">
              <div class="software-item__header">
                <!-- App Name -->
                <h3
                  class="software-item__name"
                  :title="app.displayName || app.name">
                  {{ app.displayName || app.name }}
                </h3>

                <!-- Delete App Action -->
                <div
                  class="software-item__delete"
                  @click="removeApplication(app.id)">
                  <Trash :size="16" />
                </div>
              </div>

              <!-- Licenses Section -->
              <div class="software-item__licenses">
                <span class="software-item__licenses-label">
                  {{ i18n.t('licenseLabel') }}:
                </span>
                <div
                  v-if="app.licenses.length > 0"
                  class="software-item__badges">
                  <span
                    v-for="license in app.licenses"
                    :key="license.id"
                    class="license-badge"
                    :title="license.name">
                    {{ license.name }}
                    <div
                      class="license-badge__remove"
                      @click.stop="
                        removeLicense(app.id, license.subAccountId, license.id)
                      ">
                      <X :size="12" />
                    </div>
                  </span>
                </div>
                <span v-else class="software-item__no-licenses">
                  {{ i18n.t('none') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-tab-pane>

    <!-- People Tab -->
    <el-tab-pane name="users" :label="i18n.t('people')">
      <SmTable :data="tableAccounts" :columns="accountColumns" key-field="_id">
        <template #name="{ row }">
          <div class="avatar-row">
            <SmAvatar
              :name="row.name || row.email"
              size="small"
              class="bg-gray-200" />
            <TextMultiline :lines-to-show="2">
              <span>{{ row.name }}</span>
            </TextMultiline>
          </div>
        </template>
        <template #tags="{ row }">
          <div class="tag-wrapper">
            <TagComponent
              v-for="tag in getTagsByIds(row.tags)"
              :key="tag._id"
              :tag="tag" />
          </div>
        </template>
        <template #action="{ row }">
          <SmDropdown trigger="click">
            <SmDropdownItem
              v-require-permission="CompanyAccessRole.ADMIN"
              :icon="Trash"
              @click="removeAccount(row)">
              {{ i18n.t('removeAccount') }}
            </SmDropdownItem>
          </SmDropdown>
        </template>
      </SmTable>
    </el-tab-pane>
  </el-tabs>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import {
    AccountGroup,
    Account,
    CompanyAccessRole,
    SoftwareLicense,
    PriceInformation,
    SoftwareOut,
    Tag,
    UserGroupsService,
    SoftwareTypes,
  } from '@/client'
  import { getApiErrorMessage } from '@/utils/errors'
  import { sendToast } from '@/components/sm/SmNotification'
  import { Column } from '@/components/sm/SmTable.types'
  import { Trash, Package, X } from 'lucide-vue-next'

  const i18n = useI18n()
  const emit = defineEmits<{ groupChanged: [] }>()

  const activeTab = ref('software')

  const props = defineProps<{
    group: AccountGroup
    allAccounts: Account[]
    allApplications: SoftwareOut[]
    allTags: Tag[]
    allSoftwareLicenses: SoftwareLicense[]
    allContracts: PriceInformation[]
  }>()

  // --- Data Models ---

  interface AssignedLicense {
    id: string
    name: string
    subAccountId?: string
  }

  interface AssignedApplication {
    id: string
    name: string
    displayName?: string
    type: SoftwareTypes | null
    licenses: AssignedLicense[]
  }

  // --- Computeds ---

  const applicationsMap = computed(() => {
    const map = new Map<string, SoftwareOut>()
    props.allApplications.forEach((app) => map.set(app._id, app))
    return map
  })

  const licensesMap = computed(() => {
    const map = new Map<string, SoftwareLicense>()
    props.allSoftwareLicenses.forEach((l) => map.set(l._id, l))
    return map
  })

  const assignedSoftware = computed<AssignedApplication[]>(() => {
    const result: AssignedApplication[] = []
    const permissions = props.group.permission?.software_permissions || {}

    for (const [appId, appPerm] of Object.entries(permissions)) {
      if (!appPerm) continue

      const app = applicationsMap.value.get(appId)
      if (!app) continue

      const licenses: AssignedLicense[] = []

      // Collect licenses from sub-account permissions
      if (appPerm.sub_account_permissions) {
        for (const [subAccountId, subPerm] of Object.entries(
          appPerm.sub_account_permissions
        )) {
          if (subPerm.software_license_permissions) {
            for (const licenseId of Object.keys(
              subPerm.software_license_permissions
            )) {
              const licenseModel = licensesMap.value.get(licenseId)
              if (licenseModel) {
                licenses.push({
                  id: licenseId,
                  name: licenseModel.name,
                  subAccountId,
                })
              }
            }
          }
        }
      }

      // Sort licenses by name
      licenses.sort((a, b) => a.name.localeCompare(b.name))

      result.push({
        id: app._id,
        name: app.software_name,
        displayName: app.display_name,
        type: app.type,
        licenses,
      })
    }

    // Sort applications by name
    return result.sort((a, b) => a.name.localeCompare(b.name))
  })

  const groupAccountIds = computed(() => new Set(props.group.accounts))

  const tableAccounts = computed(() => {
    return props.allAccounts.filter((a) => groupAccountIds.value.has(a._id))
  })

  const accountColumns: Column<Account>[] = [
    {
      key: 'name',
      label: i18n.t('name'),
      sortable: true,
      width: 2,
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      width: 3,
    },
    {
      key: 'tags',
      label: 'Tags',
      sortable: false,
      width: 2,
    },
    {
      key: 'action',
      label: '',
      sortable: false,
      width: '50px',
    },
  ]

  // --- Methods ---

  const tagsMap = computed(() => {
    const map = new Map<string, Tag>()
    props.allTags.forEach((tag) => map.set(tag._id, tag))
    return map
  })

  function getTagsByIds(ids: string[]): Tag[] {
    return ids
      .map((id) => tagsMap.value.get(id))
      .filter((tag): tag is Tag => !!tag)
  }

  function removeAccount(account: Account) {
    UserGroupsService.removeAccountFromAccountGroupApiV1ManagementGroupsAccountGroupIdAccountsAccountIdDelete(
      { accountId: account._id, accountGroupId: props.group._id }
    )
      .then(() => {
        sendToast(
          i18n.t('accountRemovedSuccess', { email: account.email }),
          undefined,
          'success'
        )
        emit('groupChanged')
      })
      .catch((err) => {
        sendToast(getApiErrorMessage(err, i18n), undefined, 'error')
      })
  }

  async function updateGroupPermissions(newPermission: any) {
    try {
      await UserGroupsService.patchAccountGroupApiV1ManagementGroupsAccountGroupIdPatch(
        {
          accountGroupId: props.group._id,
          requestBody: {
            permission: newPermission,
          },
        }
      )
      sendToast(i18n.t('saved'), undefined, 'success')
      emit('groupChanged')
    } catch (err) {
      sendToast(getApiErrorMessage(err, i18n), undefined, 'error')
    }
  }

  function removeApplication(appId: string) {
    const permissions = JSON.parse(
      JSON.stringify(props.group.permission || { software_permissions: {} })
    )

    if (
      permissions.software_permissions &&
      permissions.software_permissions[appId]
    ) {
      permissions.software_permissions[appId] = null
      updateGroupPermissions(permissions)
    }
  }

  function removeLicense(
    appId: string,
    subAccountId: string | undefined,
    licenseId: string
  ) {
    if (!subAccountId) return

    const permissions = JSON.parse(
      JSON.stringify(props.group.permission || { software_permissions: {} })
    )

    if (
      permissions.software_permissions?.[appId]?.sub_account_permissions?.[
        subAccountId
      ]?.software_license_permissions?.[licenseId]
    ) {
      permissions.software_permissions[appId].sub_account_permissions[
        subAccountId
      ].software_license_permissions[licenseId] = null
      updateGroupPermissions(permissions)
    }
  }
</script>

<style scoped lang="scss">
  .group-details-tabs {
    flex: 1;
    min-height: 0; /* Crucial for nested scrolling in flex container */
    display: flex;
    flex-direction: column;

    :deep(.el-tabs__header) {
      flex-shrink: 0;
      margin-bottom: 1rem;
      order: -1; /* Force header to top */
    }

    :deep(.el-tabs__content) {
      flex: 1;
      overflow-y: auto;
      padding-right: 4px; // For scrollbar
      order: 0;
    }
  }

  .software-list {
    display: flex;
    flex-direction: column;

    &__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem 1rem;
      text-align: center;
    }

    &__empty-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background-color: var(--sm-gray-100);
      margin-bottom: 1rem;
      opacity: 0.6;
    }

    &__empty-text {
      margin: 0;
      font-size: 0.875rem;
      color: var(--sm-contrast-muted);
    }

    &__items {
      display: flex;
      flex-direction: column;
    }
  }

  .software-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid var(--el-border-color-light);
    border-radius: 8px;
    margin-bottom: 0.75rem;
    background-color: var(--sm-card-bg);
    transition: all 0.2s ease;

    &__icon-wrapper {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 8px;
      background-color: var(--sm-gray-50);
      border: 1px solid var(--el-border-color-lighter);
    }

    &__icon {
      width: 26px;
      height: 26px;
    }

    &__info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding-top: 2px;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.5rem;
    }

    &__name {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--sm-contrast);
      line-height: 1.4;
      word-break: break-word;
    }

    &__delete {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--sm-contrast-muted);
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s ease;

      &:hover {
        background-color: var(--sm-magenta-10);
        color: var(--sm-magenta);
      }
    }

    &__licenses {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-top: 0.5rem;
      padding-left: 0; /* Remove left padding */
    }

    &__licenses-label {
      font-size: 0.7rem;
      font-weight: 600;
      color: var(--sm-contrast-muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    &__badges {
      display: flex;
      flex-direction: column;
      gap: 0.125rem; /* Smaller spacing between license items */
      align-items: flex-start;
    }

    &__no-licenses {
      font-size: 0.8125rem;
      color: var(--sm-contrast-muted);
      font-style: italic;
    }
  }

  .license-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.375rem;
    background-color: var(--sm-gray-100);
    color: var(--sm-contrast-secondary);
    border: 1px solid transparent;
    border-radius: 4px;
    font-size: 0.8125rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    line-height: 1.4;

    &__remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      padding: 2px;
      border-radius: 50%;
      cursor: pointer;
      opacity: 0.6;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
        opacity: 1;
        color: var(--sm-magenta);
      }
    }
  }

  .avatar-row {
    display: grid;
    grid-template-columns: 40px auto;
    gap: 1rem;
    align-items: center;
  }

  .tag-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 5px;
    padding: 0.5rem 0 0.5rem 0;
    align-items: center;
    width: 100%;
  }

  // Dark mode
  .dark {
    .software-list {
      &__empty-icon {
        background-color: var(--sm-gray-800);
      }
    }

    .software-item {
      background-color: var(--sm-gray-800);
      border-color: var(--el-border-color-darker);

      &:hover {
        border-color: var(--el-border-color);
      }

      &__icon-wrapper {
        background-color: var(--sm-gray-700);
        border-color: var(--el-border-color);
      }
    }

    .license-badge {
      background-color: var(--sm-gray-700);
      border-color: var(--sm-gray-700);
      color: var(--sm-contrast-secondary);
    }
  }
</style>
